import { useState } from "react";
import { Loader2, Eye, EyeOff, UserX, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const MODES = {
  deactivate: {
    title: "Deactivate my account", verb: "Deactivate", icon: UserX, url: "/auth/deactivate", tone: "bg-zinc-900",
    text: "You'll be signed out and can't sign in again until an owner re-enables your account. Your library, playlists and wishlist are kept.",
    done: "Your account is deactivated. An owner can re-enable it anytime.",
  },
  delete: {
    title: "Delete my account", verb: "Delete forever", icon: Trash2, url: "/auth/delete-account", tone: "bg-red-600",
    text: "This permanently removes your account, library index, playlists, wishlist, radio stations and OneDrive link from Resonance. Files in your OneDrive are not touched. This can't be undone.",
    done: "Your account has been deleted.",
  },
};

const ConfirmDialog = ({ mode, onClose }) => {
  const { logout } = useAuth();
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const m = MODES[mode] || MODES.deactivate;
  const run = async () => {
    setBusy(true);
    try {
      await api.post(m.url, { password: pw });
      toast.success(m.done);
      await logout();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={!!mode} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="self-account-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading text-2xl">{m.title}</DialogTitle></DialogHeader>
        <p className="-mt-1 text-sm text-zinc-600" data-testid="self-account-dialog-text">{m.text}</p>
        <label className="mt-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Confirm with your password</label>
        <div className="relative">
          <Input type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} autoFocus placeholder="Current password"
            data-testid="self-account-password-input" className="pr-10" onKeyDown={(e) => e.key === "Enter" && pw && run()} />
          <button onClick={() => setShow(!show)} data-testid="self-account-password-show" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-900">{show ? <EyeOff size={15} /> : <Eye size={15} />}</button>
        </div>
        <DialogFooter>
          <button onClick={onClose} disabled={busy} data-testid="self-account-cancel" className="rounded-full border border-zinc-300 px-5 py-2 text-sm hover:border-zinc-900">Cancel</button>
          <button onClick={run} disabled={busy || !pw} data-testid="self-account-confirm" className={`flex items-center gap-2 rounded-full px-6 py-2 text-sm text-white disabled:opacity-40 ${m.tone}`}>
            {busy && <Loader2 size={14} className="animate-spin" />}{m.verb}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const DangerZone = () => {
  const { user } = useAuth();
  const [mode, setMode] = useState(null);
  if (user?.role === "owner") return null;
  return (
    <section className="mt-8 rounded-lg border border-red-100 bg-white p-8" data-testid="danger-zone">
      <h2 className="text-lg font-medium">Deactivate or delete</h2>
      <p className="mt-1 text-sm text-zinc-500">Deactivating keeps everything but blocks sign-in until an owner re-enables you. Deleting removes your account and data for good.</p>
      <div className="mt-5 flex flex-wrap gap-3">
        <button onClick={() => setMode("deactivate")} data-testid="self-deactivate-button" className="flex h-10 items-center gap-2 rounded-full border border-zinc-300 px-5 text-sm transition-colors hover:border-zinc-900"><UserX size={14} />Deactivate my account</button>
        <button onClick={() => setMode("delete")} data-testid="self-delete-button" className="flex h-10 items-center gap-2 rounded-full border border-red-200 px-5 text-sm text-red-600 transition-colors hover:bg-red-600 hover:text-white"><Trash2 size={14} />Delete my account</button>
      </div>
      <ConfirmDialog mode={mode} onClose={() => setMode(null)} />
    </section>
  );
};
